import { useEffect, useState } from "react";
import { api } from "../data";
import { t } from "../lib/sprache";
import { parseQuantityInput } from "../lib/betrag";
import { Dialog } from "./ui";
import { toast } from "./Toast";

/**
 * Block B (Inhaber 15.10.2026, 0269): Steuerinhaltliche Prüfungen sperren nicht mehr, sie fragen nach. Die Datenbank meldet
 * „confirm:tax_check:<Prüfung>:<Rechnung>“ mit Rechtsgrund; dieser Dialog zeigt die Meldung, lässt die Prüfung ausdrücklich
 * bestätigen (optional mit Begründung, beim fehlenden EZB-Kurs mit eigenem Kurs, Tag und Quelle), hält das an der Rechnung fest
 * (tax_override_codes, protokolliert über invoices_audit) und wiederholt denselben Schritt. Ein Dialog für das ganze Portal:
 * useAction gibt jede solche Meldung hierher (Festschreiben, Generator, Freigabe, Einreichen im Creator-Portal).
 * K-396 (0298): Fehlt für einen deutschen Aussteller der BMF-Monatskurs des Leistungsmonats (bmf_rate_missing), bietet der
 * Dialog den EZB-Tageskurs (nur mit Gestattung des Finanzamts, § 16 Abs. 6 Satz 3 UStG; Prüfung eur_rate_ecb_day an der
 * Rechnung) oder einen eigenen Kurs an; „Abbrechen“ heisst dort: nach Monatsende festschreiben.
 */
interface Offen {
  code: string;
  invoiceId: string;
  message: string;
  retry: () => Promise<unknown>;
}

let zeigen: ((o: Offen | null) => void) | null = null;

/** Englische Fassung der Meldung, wo die Datenbank nur deutsch meldet (0444) */
const MELDUNG_EN: Record<string, string> = {
  issuer_no_eu_vat: "The issuer is based outside the EU and has no VAT ID of an EU member state. Reverse charge to a business in the EU is correct if the issuer is not registered for VAT in any EU member state; the recipient owes the VAT (Art. 196 Directive 2006/112/EC). The home country tax number appears on the invoice under its own name (e.g. TRN), never as an EU VAT ID. If the issuer is registered in an EU member state, please enter that VAT ID. Otherwise confirm “no EU registration” here; you are responsible for the tax details (CreatorOffice does not provide tax advice).",
  // 0445 (Steuerprüfung L6)
  vat_id_invalid: "The recipient's VAT ID is invalid according to VIES. Without a valid VAT ID, reverse charge is not secured; the service may then be taxable in the issuer's country and the issuer may owe the VAT. Please clarify the number with the recipient and check again. If, after checking, you still want to issue the invoice with reverse charge, confirm this here; the VIES result is recorded on the invoice and you are responsible for the tax details (CreatorOffice does not provide tax advice).",
};

const HINT = /^confirm:tax_check:([a-z_]+):([0-9a-f-]{36})$/;

/** Ist der Fehler eine Steuer-Rückfrage? Dann öffnet sich der Dialog und der Schritt läuft nach der Bestätigung erneut. */
export function steuerRueckfrageAus(e: unknown, retry: () => Promise<unknown>): boolean {
  const hint = (e as { hint?: string } | null)?.hint ?? "";
  const m = HINT.exec(hint);
  if (!m || !zeigen) return false;
  zeigen({ code: m[1]!, invoiceId: m[2]!, message: (e as Error).message, retry });
  return true;
}

/** Bezeichnung der Prüfung im Protokoll und im Dialogtitel */
export function steuerPruefungLabel(code: string): string {
  switch (code) {
    case "small_business_vat": return t("Steuer trotz Kleinunternehmerregelung", "VAT despite small business scheme");
    case "recipient_vat_id": return t("Reverse Charge ohne USt-IdNr. des Empfängers", "Reverse charge without the recipient's VAT ID");
    case "issuer_vat_id": return t("Reverse Charge ohne UID des Ausstellers", "Reverse charge without the issuer's VAT ID");
    case "small_business_abroad": return t("Kleinunternehmer-Hinweis an einen Empfänger im Ausland", "Small business note to a recipient abroad");
    case "issuer_tax_id": return t("Steuer-ID der Agentur fehlt", "Agency tax ID missing");
    case "ch_vat_number": return t("MWST ohne MWST-Nummer", "Swiss VAT without a VAT number");
    case "eur_rate_missing": return t("Kurs für die Umsatzsteuer in Euro fehlt", "Rate for VAT in euros missing");
    case "bmf_rate_missing": return t("BMF-Monatskurs noch nicht veröffentlicht", "German monthly VAT conversion rate not yet published");
    case "eur_rate_ecb_day": return t("EZB-Tageskurs mit Gestattung des Finanzamts", "ECB daily rate permitted by the tax office");
    case "recipient_private": return t("Reverse Charge an eine Privatperson", "Reverse charge to a private individual");
    case "small_business_status": return t("Kleinunternehmer-Hinweis ohne Kleinunternehmer-Status", "Small business note without small business status");
    case "tax_treatment": return t("Steuer an einen Empfänger im Ausland", "VAT to a recipient abroad");
    // 0444 (Steuerprüfung L7): Aussteller außerhalb der EU ohne USt-IdNr. eines Mitgliedstaats
    case "issuer_no_eu_vat": return t("Keine EU-Registrierung des Ausstellers", "No EU VAT registration of the issuer");
    // 0445 (Steuerprüfung L6): USt-IdNr. des Empfängers laut VIES ungültig
    case "vat_id_invalid": return t("USt-IdNr. des Empfängers laut VIES ungültig", "Recipient's VAT ID invalid according to VIES");
    default: return code;
  }
}

export function SteuerRueckfrageHost() {
  const [offen, setOffen] = useState<Offen | null>(null);
  const [geprueft, setGeprueft] = useState(false);
  const [note, setNote] = useState("");
  const [kurs, setKurs] = useState("");
  const [tag, setTag] = useState("");
  const [quelle, setQuelle] = useState("");
  const [wahl, setWahl] = useState<"ezb" | "eigen">("ezb");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    zeigen = (o) => { setOffen(o); setGeprueft(false); setNote(""); setKurs(""); setTag(""); setQuelle(""); setWahl("ezb"); };
    return () => { zeigen = null; };
  }, []);
  if (!offen) return null;
  const bmfFehlt = offen.code === "bmf_rate_missing";
  // Eigener Kurs: beim fehlenden EZB-Kurs immer, beim fehlenden BMF-Monatskurs nach Wahl
  const kursFehlt = offen.code === "eur_rate_missing" || (bmfFehlt && wahl === "eigen");

  const bestaetigen = async () => {
    if (!geprueft) return toast(t("Bitte bestätigen, dass du die Angaben geprüft hast.", "Please confirm that you have checked the details."), "alert");
    let rate: number | null = null;
    if (kursFehlt) {
      rate = parseQuantityInput(kurs, { maxDecimals: 6 });
      if (!rate || rate <= 0 || !/^\d{4}-\d{2}-\d{2}$/.test(tag) || quelle.trim().length < 3) {
        return toast(t("Bitte Kurs (1 EUR = … in der Rechnungswährung), Tag und Quelle angeben.", "Please enter the rate (1 EUR = … in the invoice currency), date and source."), "alert");
      }
    }
    setBusy(true);
    try {
      const row = (await api.select<{ tax_override_codes: string[] | null }>("invoices", { columns: "tax_override_codes", filters: [["id", "eq", offen.invoiceId]] }))[0];
      const codes = [...new Set([...(row?.tax_override_codes ?? []), offen.code, ...(bmfFehlt && wahl === "ezb" ? ["eur_rate_ecb_day"] : [])])];
      await api.update("invoices", [["id", "eq", offen.invoiceId]], {
        tax_override_confirmed_at: new Date().toISOString(), tax_override_codes: codes, tax_override_note: note.trim() || null,
        ...(kursFehlt ? { tax_eur_rate: rate, tax_eur_rate_on: tag, tax_eur_source: quelle.trim() } : {}),
      });
      const retry = offen.retry;
      setOffen(null);
      await retry();
    } catch (e) {
      toast((e as Error).message, "alert");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => setOffen(null)} title={t(`Bitte prüfen: ${steuerPruefungLabel(offen.code)}`, `Please check: ${steuerPruefungLabel(offen.code)}`)}>
      <div className="stack">
        <p role="alert">{t(offen.message, MELDUNG_EN[offen.code] ?? offen.message)}</p>
        {bmfFehlt && (
          <fieldset className="stack">
            <legend>{t("Kurs für diese Rechnung", "Rate for this invoice")}</legend>
            <label className="row tk-check"><input type="radio" name="bmf-wahl" checked={wahl === "ezb"} onChange={() => setWahl("ezb")} />
              <span>{t("EZB-Tageskurs verwenden (das Finanzamt hat den Tageskurs gestattet, § 16 Abs. 6 Satz 3 UStG)", "Use the ECB daily rate (the tax office has permitted the daily rate, § 16(6) sentence 3 UStG)")}</span></label>
            <label className="row tk-check"><input type="radio" name="bmf-wahl" checked={wahl === "eigen"} onChange={() => setWahl("eigen")} />
              <span>{t("Eigenen Kurs angeben", "Enter my own rate")}</span></label>
            <small className="muted">{t("Sonst nach Monatsende festschreiben: Das BMF veröffentlicht den Monatskurs am ersten Werktag des Folgemonats, danach trägt CreatorOffice ihn selbst ein.", "Otherwise finalise after the end of the month: the ministry publishes the monthly rate on the first working day of the following month, CreatorOffice then applies it automatically.")}</small>
          </fieldset>
        )}
        {kursFehlt && (
          <div className="grid">
            <label>{t("Kurs: 1 EUR =", "Rate: 1 EUR =")}<input inputMode="decimal" value={kurs} onChange={(e) => setKurs(e.target.value)} placeholder="0,9350" /></label>
            <label>{t("Tag des Kurses", "Date of the rate")}<input type="date" value={tag} onChange={(e) => setTag(e.target.value)} /></label>
            <label>{t("Quelle", "Source")}<input value={quelle} onChange={(e) => setQuelle(e.target.value)} placeholder={t("z. B. Monatskurs BMF Oktober 2026", "e.g. monthly rate of the finance ministry")} /></label>
          </div>
        )}
        <label>{t("Begründung (optional)", "Reason (optional)")}<textarea rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} /></label>
        <label className="row tk-check">
          <input type="checkbox" checked={geprueft} onChange={(e) => setGeprueft(e.target.checked)} />
          <span>{t("Ich habe das geprüft und verantworte diese Rechnung. CreatorOffice ist keine Steuerberatung.", "I have checked this and take responsibility for this invoice. CreatorOffice does not provide tax advice.")}</span>
        </label>
        <div className="row">
          <button type="button" className="primary" disabled={busy} onClick={() => void bestaetigen()}>{busy ? t("Wird ausgeführt …", "Running …") : t("Bestätigen und fortfahren", "Confirm and continue")}</button>
          <button type="button" disabled={busy} onClick={() => setOffen(null)}>{bmfFehlt ? t("Nach Monatsende festschreiben", "Finalise after month end") : t("Abbrechen", "Cancel")}</button>
        </div>
      </div>
    </Dialog>
  );
}
